import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const logo = `${process.env.PUBLIC_URL}/logo-stores.png`;

const PAYPAL_DONATE_URL = 'https://www.paypal.com/donate/?hosted_button_id=B5N55UVTNUZT2';
const ZELLE_EMAIL = 'donations@zivugmishamayim.com';
const DONATIONS_EMAIL = 'donations@zivugmishamayim.com';
const SUPPORT_EMAIL = 'support@zivugmishamayim.com';
const MAILTO_DONATIONS = `mailto:${DONATIONS_EMAIL}`;
const MAILTO_SUPPORT = `mailto:${SUPPORT_EMAIL}`;

const PAGE_TITLE = 'Donate — Zivug Mishamayim';
const PAGE_DESCRIPTION =
  'Support Zivug Mishamayim, a registered 501(c)(3) nonprofit helping our community build meaningful shidduch connections.';
const OG_DESCRIPTION =
  'Make a voluntary tax-deductible donation to support our community matchmaking mission.';
const CANONICAL_URL = 'https://www.zivugmishamayim.com/donate';
const OG_IMAGE = 'https://www.zivugmishamayim.com/logo-stores.png';

const GIFT_ITEMS = [
  {
    title: 'Community matchmaking',
    description:
      'Supporting programs that help singles and families build meaningful connections.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    title: 'Shidduch facilitation',
    description:
      'Resources for matchmakers and community members guiding the shidduch process.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
    ),
  },
  {
    title: 'Mission operations',
    description:
      'Technology and outreach that keep our nonprofit services available to the community.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4M12 8h.01" />
      </svg>
    ),
  },
];

function setMetaContent(selector, content) {
  const el = document.querySelector(selector);
  if (el) {
    el.setAttribute('content', content);
  }
}

async function copyEmailToClipboard(email) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(email);
      return true;
    } catch {
      // fall through to legacy copy
    }
  }

  const textarea = document.createElement('textarea');
  textarea.value = email;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();

  let copied = false;
  try {
    copied = document.execCommand('copy');
  } catch {
    copied = false;
  }

  document.body.removeChild(textarea);
  return copied;
}

function DonatePage() {
  const [zelleCopied, setZelleCopied] = useState(false);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;

    const metaUpdates = [
      ['meta[name="description"]', PAGE_DESCRIPTION],
      ['meta[property="og:title"]', PAGE_TITLE],
      ['meta[property="og:description"]', OG_DESCRIPTION],
      ['meta[property="og:url"]', CANONICAL_URL],
      ['meta[property="og:image"]', OG_IMAGE],
      ['meta[name="twitter:title"]', PAGE_TITLE],
      ['meta[name="twitter:description"]', OG_DESCRIPTION],
      ['meta[name="twitter:image"]', OG_IMAGE],
    ];

    const previousMeta = metaUpdates.map(([selector]) => {
      const el = document.querySelector(selector);
      return el?.getAttribute('content') ?? null;
    });

    metaUpdates.forEach(([selector, content]) => setMetaContent(selector, content));

    let canonical = document.querySelector('link[rel="canonical"]');
    const hadCanonical = Boolean(canonical);
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    const previousCanonical = canonical.getAttribute('href');
    canonical.setAttribute('href', CANONICAL_URL);

    window.scrollTo(0, 0);

    return () => {
      document.title = previousTitle;
      metaUpdates.forEach(([selector], index) => {
        if (previousMeta[index] !== null) {
          setMetaContent(selector, previousMeta[index]);
        }
      });
      if (hadCanonical) {
        canonical.setAttribute('href', previousCanonical || 'https://www.zivugmishamayim.com');
      } else {
        canonical.remove();
      }
    };
  }, []);

  const handleCopyZelle = useCallback(async () => {
    const copied = await copyEmailToClipboard(ZELLE_EMAIL);
    if (copied) {
      setZelleCopied(true);
      window.setTimeout(() => setZelleCopied(false), 2500);
    }
  }, []);

  return (
    <div className="App donate-page">
      <nav className="navbar">
        <div className="container">
          <div className="nav-content">
            <div className="logo">
              <Link to="/" aria-label="Zivug Mishamayim home">
                <img src={logo} alt="Zivug Mishamayim" className="logo-image donate-logo" />
              </Link>
            </div>
            <div className="nav-links">
              <Link to="/">Home</Link>
              <Link to="/donate">Donate</Link>
            </div>
          </div>
        </div>
      </nav>

      <main className="donate-main">
        <section className="donate-hero">
          <div className="container">
            <span className="donate-badge">Registered 501(c)(3) nonprofit</span>
            <h1>Support our mission</h1>
            <p className="donate-hero-subtitle">
              Zivug Mishamayim is a registered nonprofit dedicated to helping our community find
              meaningful shidduch connections.
            </p>
          </div>
        </section>

        <section className="donate-section">
          <div className="container">
            <h2 className="donate-section-title">How your gift is used</h2>
            <div className="donate-gift-grid">
              {GIFT_ITEMS.map((item) => (
                <article key={item.title} className="donate-card donate-gift-card">
                  <div className="donate-gift-icon">{item.icon}</div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="donate-section donate-ways-section">
          <div className="container">
            <h2 className="donate-section-title">Ways to give</h2>

            <article className="donate-card donate-option-card donate-option-primary">
              <div className="donate-option-header">
                <div className="donate-option-icon donate-option-icon-paypal" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 2.901C5.026 2.318 5.474 1.9 6.002 1.9h7.08c3.476 0 5.93 1.757 5.93 5.258 0 4.478-2.73 7.46-6.795 7.46h-2.29l-.852 5.719z" />
                  </svg>
                </div>
                <div>
                  <h3>Donate with PayPal or card</h3>
                  <p>Fast and secure online checkout. Credit and debit cards accepted.</p>
                </div>
              </div>
              <a
                href={PAYPAL_DONATE_URL}
                className="donate-btn donate-btn-primary"
                rel="noopener noreferrer"
              >
                Donate with PayPal or card
              </a>
            </article>

            <article className="donate-card donate-option-card">
              <div className="donate-option-header">
                <div className="donate-option-icon donate-option-icon-zelle" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                    <rect x="2" y="5" width="20" height="14" rx="2" />
                    <path d="M2 10h20" />
                  </svg>
                </div>
                <div>
                  <h3>Donate with Zelle</h3>
                  <p>Open your bank app, go to Zelle, and send your donation to the email below.</p>
                </div>
              </div>
              <div className="donate-zelle-row">
                <a href={`mailto:${ZELLE_EMAIL}`} className="donate-zelle-email">
                  {ZELLE_EMAIL}
                </a>
                <button
                  type="button"
                  className="donate-btn donate-btn-secondary"
                  onClick={handleCopyZelle}
                  aria-live="polite"
                >
                  {zelleCopied ? 'Email copied!' : 'Copy email'}
                </button>
              </div>
            </article>
          </div>
        </section>

        <section className="donate-section donate-disclosure-section">
          <div className="container">
            <article className="donate-card donate-disclosure-card">
              <h2 className="donate-section-title">Donation disclosure</h2>
              <p>
                Donations are voluntary contributions to <strong>Zivug Mishamayim</strong>, a registered
                501(c)(3) tax-exempt nonprofit organization.
              </p>
              <p>
                Donations do not unlock any app features, digital content, or services. The app is free
                for all users.
              </p>
              <p>
                Tax-deductible contributions may be available where applicable. Donation receipts are
                provided according to our payment processor policies (PayPal / Zelle).
              </p>
              <p>
                Questions:{' '}
                <a href={MAILTO_DONATIONS}>{DONATIONS_EMAIL}</a>
              </p>
              <p>
                <Link to="/privacy">Privacy policy</Link>
              </p>
            </article>
          </div>
        </section>
      </main>

      <footer className="footer donate-footer">
        <div className="container">
          <p className="donate-thank-you">Thank you for supporting our community mission.</p>
          <div className="footer-bottom">
            <p>
              <Link to="/">Home</Link>
              {' · '}
              <Link to="/privacy">Privacy policy</Link>
              {' · '}
              <Link to="/support">Support</Link>
              {' · '}
              <a href={MAILTO_SUPPORT}>Email support</a>
            </p>
            <p>&copy; 2026 Zivug Mishamayim. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default DonatePage;
